<template>
  <div class="min-h-screen bg-gray-100 p-4">
    <div class="max-w-7xl mx-auto space-y-10">
      <!-- Header / toolbar -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between mb-6">
        <h2 class="text-2xl md:text-3xl font-extrabold text-primary-dark flex items-center gap-3 flex-wrap drop-shadow-lg">
          <span class="material-symbols-outlined text-primary text-2xl md:text-3xl">trending_up</span>
          Your Video Ideas
          <!-- Category chip -->
          <span
            v-if="categoryLabel"
            class="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/30 text-primary-dark px-3 py-1 text-sm font-semibold shadow"
            :title="`Category: ${categoryLabel}`"
          >
            <span class="material-symbols-outlined text-primary text-base">category</span>
            {{ categoryLabel }}
          </span>
          <!-- Region chip -->
          <span
            v-if="regionLabel"
            class="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/30 text-primary-dark px-3 py-1 text-sm font-semibold shadow"
            :title="`Region: ${regionLabel}`"
          >
            <span class="material-symbols-outlined text-primary text-base">location_on</span>
            {{ regionLabel }}
          </span>
        </h2>

        <button style="display: none;"
          @click="$emit('refresh')"
          :disabled="loading"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-semibold
                 bg-primary text-white shadow-sm
                 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary
                 disabled:opacity-60 disabled:cursor-not-allowed transition-all mt-4 md:mt-0"
          aria-label="Refresh ideas"
        >
          <span class="material-symbols-outlined">refresh</span>
          <span v-if="!loading">Refresh</span>
          <span v-else>Refreshing…</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="space-y-6">
        <div class="flex items-center gap-2 text-primary-dark text-lg font-semibold">
          <span class="material-symbols-outlined animate-spin text-primary text-2xl">autorenew</span>
          Generating AI recommendations…
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            v-for="n in 4"
            :key="n"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
          >
            <div class="h-6 w-2/3 bg-primary/10 rounded mb-6 animate-pulse"></div>
            <div class="space-y-3">
              <div class="h-4 w-full bg-primary/10 rounded animate-pulse"></div>
              <div class="h-4 w-5/6 bg-primary/10 rounded animate-pulse"></div>
              <div class="h-4 w-3/4 bg-primary/10 rounded animate-pulse"></div>
            </div>
            <div class="mt-8 flex gap-3">
              <span class="h-7 w-20 bg-primary/10 rounded animate-pulse"></span>
              <span class="h-7 w-24 bg-primary/10 rounded animate-pulse"></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div
        v-else-if="error"
        class="bg-white rounded-lg shadow-sm border border-red-200 p-6 mb-6"
        role="alert"
      >
        <div class="flex items-start gap-4">
          <span class="material-symbols-outlined text-red-600 text-2xl mt-0.5">error</span>
          <div class="flex-1">
            <p class="text-red-800 text-base font-semibold">{{ error }}</p>
            <button
              @click="$emit('refresh')"
              class="mt-4 inline-flex items-center gap-2 text-base font-semibold text-red-700 hover:underline"
            >
              <span class="material-symbols-outlined text-base">refresh</span>
              Try again
            </button>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div
        v-else-if="hasRecs && !loading"
        class="space-y-6"
      >
        <!-- Quick summary -->
        <div class="flex flex-wrap items-center gap-4 text-lg text-gray-700 mb-2">
          <div class="inline-flex items-center gap-2">
            <span class="material-symbols-outlined text-primary">lightbulb</span>
            <span class="font-semibold text-primary-dark">{{ totalIdeas }} ideas</span>
          </div>
          <div v-if="tagSummary.length" class="inline-flex items-center gap-2">
            <span class="material-symbols-outlined text-emerald-600">sell</span>
            <span>Common tags: <span class="font-medium">{{ tagSummary.join(', ') }}</span></span>
          </div>
        </div>

        <!-- Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <article
            v-for="(rec, index) in recommendations.recommendations"
            :key="index"
            class="group relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition border-l-8"
            :class="getDifficultyColor(rec.difficulty)"
          >
            <!-- Title + engagement chip -->
            <header class="flex items-start justify-between gap-6 mb-2">
              <h3 
                class="text-lg font-bold text-gray-900 leading-snug cursor-pointer hover:text-primary transition-colors select-none"
                :title="`Click to copy: ${rec.title}`"
                @click="copyText(rec.title, 'Title')"
              >
                {{ rec.title }}
              </h3>
              <span
                class="shrink-0 rounded-full px-3 py-1 text-xs font-semibold shadow"
                :class="getEngagementColor(rec.estimatedEngagement)"
                title="Estimated engagement potential"
                style="display: none;"
              >
                {{ rec.estimatedEngagement }}
              </span>
            </header>

            <!-- Description -->
            <p 
              class="mt-3 text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors select-none"
              :title="`Click to copy: ${rec.description}`"
              @click="copyText(rec.description, 'Description')"
            >
              {{ rec.description }}
            </p>

            <!-- Meta row -->
            <dl class="mt-6 grid grid-cols-3 gap-4 text-sm">
              <div class="flex items-center gap-2 text-gray-600">
                <span class="material-symbols-outlined text-gray-500 text-lg">movie</span>
                <dt class="sr-only">Type</dt>
                <dd class="truncate" :title="rec.contentType">{{ rec.contentType }}</dd>
              </div>
              <div class="flex items-center gap-2 text-gray-600">
                <span class="material-symbols-outlined text-gray-500 text-lg">speed</span>
                <dt class="sr-only">Level</dt>
                <dd class="truncate">{{ rec.difficulty }}</dd>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-gray-500 text-lg">trending_up</span>
                <dt class="sr-only">Trend strength</dt>
                <dd
                  class="px-2 rounded font-semibold"
                  :class="getTrendColor(rec.trendStrength)"
                >
                  {{ rec.trendStrength }}
                </dd>
              </div>
            </dl>

            <!-- Tags -->
            <div class="mt-6 flex flex-wrap gap-2">
              <span
                v-for="tag in rec.tags.slice(0, 4)"
                :key="tag"
                class="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary-dark px-3 py-1 text-xs font-semibold cursor-pointer hover:bg-primary/20 transition-colors select-none"
                :title="`Click to copy: ${tag}`"
                @click="copyText(tag, 'Tag')"
              >
                <span class="material-symbols-outlined text-primary text-sm">sell</span>
                {{ tag }}
              </span>
            </div>

            <!-- Reasoning collapsible -->
            <details class="mt-6 group-open:mt-4">
              <summary class="cursor-pointer select-none text-sm text-primary-dark hover:underline inline-flex items-center gap-2 font-semibold">
                <span class="material-symbols-outlined text-base">info</span>
                Why this could work
              </summary>
              <p class="mt-3 text-sm text-gray-700">
                {{ rec.reasoning }}
              </p>
            </details>

            <!-- Inspiration Videos Section -->
            <details v-if="rec.inspirationVideos && rec.inspirationVideos.length > 0" class="mt-4">
              <summary class="cursor-pointer select-none text-sm text-primary-dark hover:underline inline-flex items-center gap-2 font-semibold">
                <span class="material-symbols-outlined text-base">video_library</span>
                Inspiration from {{ rec.inspirationVideos.length }} trending video{{ rec.inspirationVideos.length > 1 ? 's' : '' }}
              </summary>
              <div class="mt-3 space-y-3">
                <div
                  v-for="(video, vIndex) in rec.inspirationVideos"
                  :key="vIndex"
                  class="bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <div class="flex items-start gap-3">
                    <!-- YouTube Thumbnail -->
                    <div class="flex-shrink-0 relative group">
                      <a
                        :href="video.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="block relative overflow-hidden rounded-lg"
                      >
                        <img
                          :src="getYouTubeThumbnail(video.url)"
                          :alt="video.title"
                          class="w-24 h-16 sm:w-28 sm:h-20 object-cover transition-transform duration-200 group-hover:scale-105"
                          loading="lazy"
                          @error="handleThumbnailError"
                        />
                        <!-- Play overlay -->
                        <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-lg">
                          <span class="material-symbols-outlined text-white text-2xl drop-shadow-md">play_circle</span>
                        </div>
                        <!-- Duration badge (if you have duration data) -->
                        <div class="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                          <span class="material-symbols-outlined text-xs">play_arrow</span>
                        </div>
                      </a>
                    </div>
                    
                    <div class="flex-1 min-w-0">
                      <h4 class="text-sm font-medium text-gray-900 leading-tight mb-1" :title="video.title">
                        {{ truncateTitle(video.title, 60) }}
                      </h4>
                      <div class="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        <span>{{ video.channel }}</span>
                        <span>•</span>
                        <span>{{ video.views }} views</span>
                      </div>
                      <p class="text-xs text-gray-600 mb-2 leading-relaxed">{{ video.reason }}</p>
                      <div class="flex items-center gap-2">
                        <a
                          :href="video.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
                        >
                          <span class="material-symbols-outlined text-xs">open_in_new</span>
                          Watch video
                        </a>
                        <button
                          @click="copyText(video.url, 'Video URL')"
                          class="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium"
                        >
                          <span class="material-symbols-outlined text-xs">content_copy</span>
                          Copy link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>

            <!-- Actions (download only) -->
            <div class="mt-8 flex flex-wrap gap-4">
              <!-- Download idea button - now primary -->
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-full bg-primary text-white px-4 py-2 text-sm font-semibold
                       hover:bg-primary-dark shadow-sm"
                title="Download video idea as text file"
                @click="downloadIdea(rec, index)"
              >
                <span class="material-symbols-outlined text-base">download</span>
                Download Idea
              </button>
            </div>
          </article>
        </div>

        <!-- Download All Ideas Button -->
        <div class="flex justify-center mt-8">
          <button
            @click="downloadAllIdeas"
            class="inline-flex items-center gap-2 rounded-full bg-green-600 text-white px-6 py-3 text-base font-semibold
                   hover:bg-green-700 shadow-sm transition"
            title="Download all video ideas as a single text file"
          >
            <span class="material-symbols-outlined text-base">download</span>
            Download All Ideas ({{ totalIdeas }})
          </button>
        </div>

        <!-- Insights -->
        <section v-if="recommendations.insights" class="bg-white rounded-lg shadow-sm border border-blue-200/70 p-8 mt-6">
          <h3 class="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <span class="material-symbols-outlined text-blue-700">insights</span>
            Market Insights
          </h3>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h4 class="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span class="material-symbols-outlined text-amber-700">whatshot</span>
                Top Trends
              </h4>
              <ul class="space-y-2">
                <li
                  v-for="trend in (recommendations.insights.topTrends || [])"
                  :key="`trend-${trend}`"
                  class="text-base text-gray-700 flex items-start gap-2"
                >
                  <span class="material-symbols-outlined text-gray-500 text-base mt-0.5">fiber_manual_record</span>
                  <span>{{ trend }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 class="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span class="material-symbols-outlined text-emerald-700">target</span>
                Content Gaps
              </h4>
              <ul class="space-y-2">
                <li
                  v-for="gap in (recommendations.insights.contentGaps || [])"
                  :key="`gap-${gap}`"
                  class="text-base text-gray-700 flex items-start gap-2"
                >
                  <span class="material-symbols-outlined text-gray-500 text-base mt-0.5">fiber_manual_record</span>
                  <span>{{ gap }}</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 class="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span class="material-symbols-outlined text-indigo-700">schedule</span>
                Best Upload Times
              </h4>
              <p class="text-base text-gray-700">
                {{ recommendations.insights.bestUploadTimes }}
              </p>
            </div>

            <div>
              <h4 class="flex items-center gap-2 font-semibold text-gray-800 mb-3">
                <span class="material-symbols-outlined text-fuchsia-700">group</span>
                Audience Insights
              </h4>
              <p class="text-base text-gray-700">
                {{ recommendations.insights.audienceInsights }}
              </p>
            </div>
          </div>
        </section>
      </div>

      <!-- Empty -->
      <div v-else class="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center flex flex-col items-center">
        <div class="mx-auto mb-4 h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <span class="material-symbols-outlined text-primary text-3xl">lightbulb_circle</span>
        </div>
        <p class="text-gray-900 font-bold text-lg">No recommendations yet</p>
        <p class="text-base text-gray-600 mt-2">Use the Analyze button above to generate fresh ideas.</p>
        <button
          @click="$emit('refresh')"
          class="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-white px-6 py-3 text-base font-semibold hover:bg-primary-dark shadow-sm"
        >
          <span class="material-symbols-outlined text-base">refresh</span>
          Generate Ideas
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'VideoRecommendations',
  props: {
    recommendations: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    error: { type: String, default: null },
    categoryLabel: { type: String, default: '' },
    regionLabel: { type: String, default: '' }
  },
  emits: ['refresh'],
  computed: {
    hasRecs() {
      return (
        this.recommendations &&
        Array.isArray(this.recommendations.recommendations) &&
        this.recommendations.recommendations.length > 0 &&
        !this.loading // Add this check to prevent showing during loading
      );
    },
    totalIdeas() {
      return this.hasRecs ? this.recommendations.recommendations.length : 0;
    },
    engagementSummary() {
      if (!this.hasRecs) return '';
      const counts = { High: 0, Medium: 0, Low: 0 };
      for (const r of this.recommendations.recommendations) {
        counts[r.estimatedEngagement] = (counts[r.estimatedEngagement] || 0) + 1;
      }
      return `High ${counts.High || 0} · Medium ${counts.Medium || 0} · Low ${counts.Low || 0}`;
    },
    tagSummary() {
      if (!this.hasRecs) return [];
      const freq = new Map();
      this.recommendations.recommendations.forEach(r => {
        (r.tags || []).slice(0, 2).forEach(t => {
          freq.set(t, (freq.get(t) || 0) + 1);
        });
      });
      return [...freq.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([k]) => k);
    }
  },
  methods: {
    ...mapActions(['showAlert']),
    getDifficultyColor(difficulty) {
      const map = {
        Beginner: 'border-green-400',
        Intermediate: 'border-yellow-400',
        Advanced: 'border-red-400'
      };
      return map[difficulty] || 'border-gray-300';
    },
    getEngagementColor(level) {
      const map = {
        Low: 'bg-gray-100 text-gray-700',
        Medium: 'bg-amber-100 text-amber-700',
        High: 'bg-emerald-100 text-emerald-700'
      };
      return map[level] || 'bg-gray-100 text-gray-700';
    },
    getTrendColor(strength) {
      const map = {
        Emerging: 'bg-blue-100 text-blue-700',
        Growing: 'bg-emerald-100 text-emerald-700',
        Peak: 'bg-rose-100 text-rose-700',
        Declining: 'bg-gray-100 text-gray-700'
      };
      return map[strength] || 'bg-gray-100 text-gray-700';
    },
    downloadIdea(idea, index) {
      const content = this.formatIdeaForDownload(idea, index);
      const filename = this.sanitizeFilename(`${idea.title}_video_idea.txt`);
      this.downloadTextFile(content, filename);
    },

    downloadAllIdeas() {
      if (!this.hasRecs) return;

      const content = this.recommendations.recommendations.map((idea, index) => {
        return `=== VIDEO IDEA ${index + 1} ===\n\n${this.formatIdeaForDownload(idea, index)}\n\n`;
      }).join('\n');

      const regionName = this.regionLabel || 'Global';
      const categoryName = this.categoryLabel || 'All Categories';
      const filename = this.sanitizeFilename(`video_ideas_${regionName}_${categoryName}.txt`);
      
      this.downloadTextFile(content, filename);
    },

    formatIdeaForDownload(idea, index) {
      const lines = [
        `AI-GENERATED VIDEO IDEA #${index + 1}`,
        `Generated on: ${new Date().toLocaleString()}`,
        `Region: ${this.regionLabel || 'Global'}`,
        `Category: ${this.categoryLabel || 'All Categories'}`,
        '',
        `TITLE: ${idea.title}`,
        '',
        `DESCRIPTION:`,
        idea.description,
        '',
        `CONTENT TYPE: ${idea.contentType}`,
        `DIFFICULTY LEVEL: ${idea.difficulty}`,
        `TREND STRENGTH: ${idea.trendStrength}`,
        `ESTIMATED ENGAGEMENT: ${idea.estimatedEngagement}`,
        '',
        `TAGS:`,
        idea.tags.map(tag => `• ${tag}`).join('\n'),
        '',
        `WHY THIS COULD WORK:`,
        idea.reasoning,
        ''
      ];

      // Add inspiration videos section if available
      if (idea.inspirationVideos && idea.inspirationVideos.length > 0) {
        lines.push(
          `INSPIRATION FROM TRENDING VIDEOS:`,
          ...idea.inspirationVideos.map((video, i) => [
            `${i + 1}. "${video.title}" by ${video.channel}`,
            `   Views: ${video.views} | URL: ${video.url}`,
            `   Why it inspired this idea: ${video.reason}`,
            ''
          ].join('\n')),
          ''
        );
      }

      lines.push(
        `CONTENT CREATION TIPS:`,
        `• Focus on the trending elements that make this idea popular`,
        `• Ensure your content matches the difficulty level (${idea.difficulty})`,
        `• Optimize for ${idea.estimatedEngagement.toLowerCase()} engagement potential`,
        `• Consider the content type: ${idea.contentType}`,
        idea.inspirationVideos?.length > 0 ? `• Study the inspiration videos to understand what resonates with audiences` : '',
        '',
        `SEO KEYWORDS TO TARGET:`,
        this.extractKeywordsFromIdea(idea).join(', '),
        '',
        `RECOMMENDED POSTING STRATEGY:`,
        this.getPostingStrategy(idea.trendStrength),
        '',
        `---`
      );

      return lines.filter(line => line !== '').join('\n');
    },

    extractKeywordsFromIdea(idea) {
      // Extract keywords from title, description, and tags
      const allText = `${idea.title} ${idea.description} ${idea.tags.join(' ')}`;
      const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'how', 'what', 'when', 'where', 'why', 'who'];
      
      return allText
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // Remove punctuation
        .split(' ')
        .filter(word => word.length > 3 && !commonWords.includes(word))
        .filter((word, index, arr) => arr.indexOf(word) === index) // Remove duplicates
        .slice(0, 10); // Top 10 keywords
    },

    getPostingStrategy(trendStrength) {
      const strategies = {
        'Emerging': 'Post immediately while the trend is still growing. You have a chance to be an early adopter.',
        'Growing': 'This is prime time! Post within the next few days to catch the trend at its peak growth.',
        'Peak': 'Act fast! The trend is at its highest point. Post immediately but be prepared for high competition.',
        'Declining': 'Consider a unique angle or wait for the trend to cycle back. Focus on evergreen content instead.'
      };
      
      return strategies[trendStrength] || 'Post when you have quality content ready. Focus on your unique perspective.';
    },

    sanitizeFilename(filename) {
      return filename
        .replace(/[^\w\s.-]/g, '') // Remove special characters except dots, hyphens, spaces
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_{2,}/g, '_') // Replace multiple underscores with single
        .substring(0, 100); // Limit length
    },

    downloadTextFile(content, filename) {
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);

      // Show success message using Vuex action
      this.showAlert({
        message: `Downloaded: ${filename}`,
        type: 'success'
      });
    },
    copyText(text, type = 'Text') {
      if (!text) return;
      
      navigator.clipboard?.writeText(text).then(() => {
        // Show alert using Vuex action
        this.showAlert({
          message: `${type} copied to clipboard!`,
          type: 'success'
        });
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        this.showAlert({
          message: `${type} copied to clipboard!`,
          type: 'success'
        });
      });
    },

    /**
     * Extract YouTube video ID from URL and return thumbnail URL
     */
    getYouTubeThumbnail(videoUrl) {
      if (!videoUrl) return '';

      // Extract video ID from various YouTube URL formats
      const videoId = this.extractYouTubeVideoId(videoUrl);
      
      if (!videoId) {
        // Fallback to a default thumbnail if we can't extract ID
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0NUw3MiAzNlY1NEw0OCA0NVoiIGZpbGw9IiM2QjdCODAiLz4KPC9zdmc+';
      }

      // Use YouTube's medium quality thumbnail (320x180)
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    },

    /**
     * Extract video ID from YouTube URL
     */
    extractYouTubeVideoId(url) {
      if (!url) return null;

      // Handle different YouTube URL formats
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
        /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
          return match[1];
        }
      }

      return null;
    },

    /**
     * Truncate title to specified length
     */
    truncateTitle(title, maxLength = 50) {
      if (!title) return '';
      if (title.length <= maxLength) return title;
      return title.substring(0, maxLength) + '...';
    },

    /**
     * Handle thumbnail loading errors
     */
    handleThumbnailError(event) {
      // Fallback to a default image if YouTube thumbnail fails to load
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0NUw3MiAzNlY1NEw0OCA0NVoiIGZpbGw9IiM2QjdCODAiLz4KPC9zdmc+';
    },

    // ...existing code...
  }
  // ...existing code...
};
</script>

<style scoped>
/* Premium look enhancements */
.bg-white\/80 {
  background: rgba(255,255,255,0.8);
}
.backdrop-blur-lg {
  backdrop-filter: blur(16px);
}
.shadow-sm {
  box-shadow: 0 2px 8px 0 rgba(80,36,255,0.06);
}
.shadow-md {
  box-shadow: 0 4px 16px 0 rgba(80,36,255,0.08);
}
/* Match UserProfile.vue card rounding */
.rounded-lg {
  border-radius: 0.75rem;
}
/* Match UserProfile.vue border color */
.border-gray-200 {
  --tw-border-opacity: 1;
  border-color: rgb(229 231 235 / var(--tw-border-opacity));
}
/* Match UserProfile.vue background color */
.bg-gray-100 {
  --tw-bg-opacity: 1;
  background-color: rgb(243 244 246 / var(--tw-bg-opacity));
}

/* Enhance hover effects */
.cursor-pointer:hover {
  transform: scale(1.01);
}

.cursor-pointer:active {
  transform: scale(0.99);
}

/* Additional styles for better thumbnail display */
.group:hover .group-hover\:scale-105 {
  transform: scale(1.05);
}

/* Ensure thumbnails maintain aspect ratio */
img[alt*="title"] {
  aspect-ratio: 16/9;
  object-fit: cover;
}
</style>
